import React, { useState, useEffect, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  UserPlus, Building2, KeyRound, ClipboardCheck, ArrowLeft, ArrowRight,
  Mail, ShieldCheck, Sparkles, Eye, EyeOff,
} from "lucide-react";

const ALL_ROLES = ["Staff", "House Manager", "Administration", "Project Manager", "COO", "CEO"];
// Per company structure: classification controls grouping/display, access_role controls
// permissions. CEO, Project Manager and COO are access roles ONLY (not classifications).
const ALL_CLASSIFICATIONS = [
  "Direct Care Worker",
  "Contract Care Worker",
  "Administration",
  "House Manager",
];
// Caregiver Team — Direct + Contract only. Everything else (Administration, House Manager)
// is part of the Administration Team and lives on Users & Access, not on caregiver views.
const CAREGIVER_CLASSIFICATIONS = ["Direct Care Worker", "Contract Care Worker"];
const GENDERS = ["Male", "Female"];
const DEFAULT_TEMP_PWD = "123455";

const STEPS = [
  { id: 1, label: "Basic info",      icon: UserPlus },
  { id: 2, label: "Home assignment", icon: Building2 },
  { id: 3, label: "Login setup",     icon: KeyRound },
  { id: 4, label: "Review",          icon: ClipboardCheck },
];

export default function StaffOnboardingWizard({ onClose, onCreated }) {
  const { user } = useAuth();
  const [homes, setHomes] = useState([]);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  const accessibleHomes = useMemo(() => {
    if (user.access_role === "House Manager") {
      return user.assigned_homes?.length ? user.assigned_homes
           : (user.assigned_home ? [user.assigned_home] : []);
    }
    return homes.map((h) => h.home_name);
  }, [homes, user]);

  const visibleRoles = useMemo(() => {
    // CEO-only role assignment, plus HMs cannot promote anyone above HM.
    if (user.access_role === "CEO") return ALL_ROLES;
    if (user.access_role === "House Manager") return ["Staff", "House Manager"];
    return ALL_ROLES.filter((r) => r !== "CEO");
  }, [user]);

  const initialHome = user.access_role === "House Manager"
    ? (accessibleHomes[0] || "") : "";

  const [data, setData] = useState({
    full_name: "", email: "", phone: "", gender: "",
    staff_classification: "Direct Care Worker",
    access_role: "Staff",
    assigned_home: initialHome,
    active_status: true,
    staff_id_label: "",
    start_date: "",
    password: DEFAULT_TEMP_PWD,
    send_welcome_email: true,
    force_password_change: true,
  });

  const update = (k, v) => setData((d) => {
    const next = { ...d, [k]: v };
    // When the admin changes access_role, default the classification to a sensible
    // value that matches the company structure. They can still override it manually.
    if (k === "access_role") {
      const presetByRole = {
        "Staff": "Direct Care Worker",
        "House Manager": "House Manager",
        "Administration": "Administration",
        "Project Manager": "Administration",
        "CEO": "Administration",
      };
      next.staff_classification = presetByRole[v] || d.staff_classification;
    }
    return next;
  });

  useEffect(() => {
    api.get("/homes").then(({ data: d }) => setHomes((d || []).filter((h) => h.active_status)));
  }, []);

  // Snap home to user's first accessible home once we've fetched homes (HM safety net).
  useEffect(() => {
    if (user.access_role === "House Manager" && !data.assigned_home && accessibleHomes.length > 0) {
      update("assigned_home", accessibleHomes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessibleHomes.length]);

  const isCaregiver = CAREGIVER_CLASSIFICATIONS.includes(data.staff_classification);
  // Caregivers MUST be assigned to a real home (they appear in Score Entry / Leaderboard).
  // Admin / management staff can also be assigned a home (e.g. an HM owns a home), but it's
  // not strictly required because their visibility is on Users & Access, not the Staff Directory.
  const requiresHome = isCaregiver || data.access_role === "Staff" || data.access_role === "House Manager";
  const stepValid = useMemo(() => {
    if (step === 1) {
      return data.full_name.trim().length > 1
          && /\S+@\S+\.\S+/.test(data.email)
          && Boolean(data.staff_classification)
          && Boolean(data.access_role);
    }
    if (step === 2) {
      return !requiresHome || Boolean(data.assigned_home);
    }
    if (step === 3) {
      return data.password.trim().length >= 6;
    }
    return true;
  }, [step, data, requiresHome]);

  const next = () => setStep((s) => Math.min(4, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        full_name: data.full_name.trim(),
        email: data.email.trim().toLowerCase(),
        phone: data.phone.trim() || undefined,
        gender: data.gender || undefined,
        staff_classification: data.staff_classification,
        access_role: data.access_role,
        assigned_home: requiresHome ? data.assigned_home : "",
        active_status: data.active_status,
        staff_id_label: data.staff_id_label.trim() || undefined,
        start_date: data.start_date || undefined,
        password: data.password.trim(),
        send_welcome_email: data.send_welcome_email,
        force_password_change: data.force_password_change,
      };
      const { data: created } = await api.post("/users", payload);
      setCreatedUser(created);
      onCreated?.(created);
      toast.success(`${created.full_name} onboarded${created.welcome_email_sent ? " · welcome email sent" : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create staff");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForAnother = () => {
    setCreatedUser(null);
    setStep(1);
    setData({
      full_name: "", email: "", phone: "", gender: "",
      staff_classification: "Direct Care Worker",
      access_role: "Staff",
      assigned_home: initialHome,
      active_status: true,
      staff_id_label: "",
      start_date: "",
      password: DEFAULT_TEMP_PWD,
      send_welcome_email: true,
      force_password_change: true,
    });
  };

  // ── Success view ──────────────────────────────────────────
  if (createdUser) {
    return (
      <div className="space-y-5" data-testid="wizard-success">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-900">
          <Sparkles className="text-emerald-600 mb-2" />
          <h2 className="text-h3 font-bold">Staff onboarded</h2>
          <p className="text-sm mt-2 leading-relaxed">
            <b>{createdUser.full_name}</b> ({createdUser.email}) has been created with role <b>{createdUser.access_role}</b>
            {createdUser.assigned_home ? <> and assigned to <b>{createdUser.assigned_home}</b></> : null}.
          </p>
          {createdUser.temp_password && (
            <div className="mt-4 rounded-lg bg-white/70 border border-emerald-200 p-3 text-xs">
              <p className="text-[12.5px] uppercase tracking-wider font-semibold text-emerald-700">Temporary password (one-time)</p>
              <p className="font-mono text-sm mt-1 text-emerald-950 select-all" data-testid="wizard-temp-pwd">
                {createdUser.temp_password}
              </p>
              <p className="mt-2 text-emerald-800">
                {createdUser.welcome_email_sent
                  ? "Welcome email sent to the user with this password."
                  : "Email delivery skipped or failed — copy this password and share it securely."}
                {createdUser.force_password_change ? " The user must change it on first login." : ""}
              </p>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={resetForAnother}
            className="px-4 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-sm font-semibold"
            data-testid="wizard-add-another">
            Onboard another
          </button>
          <button onClick={onClose}
            className="px-4 py-2 rounded-md bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold"
            data-testid="wizard-done">
            Done
          </button>
        </div>
      </div>
    );
  }

  // ── Step pills ────────────────────────────────────────────
  return (
    <div className="space-y-5" data-testid="onboarding-wizard">
      <div className="flex items-center gap-1 text-xs flex-wrap">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold ${
              s.id < step ? "bg-emerald-50 text-emerald-700" :
              s.id === step ? "bg-sky-100 text-sky-800" :
              "text-slate-400"
            }`} data-testid={`wiz-step-${s.id}`}>
              <s.icon size={12} /> {s.label}
            </div>
            {i < STEPS.length - 1 && <div className="w-3 h-px bg-slate-200" />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 — Basic info */}
      {step === 1 && (
        <div className="space-y-3">
          <h3 className="text-h3">Basic information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full name" required>
              <input className="form-input" autoFocus value={data.full_name}
                onChange={(e) => update("full_name", e.target.value)} data-testid="wiz-full-name" />
            </Field>
            <Field label="Email" required>
              <input type="email" className="form-input" value={data.email}
                onChange={(e) => update("email", e.target.value)} data-testid="wiz-email" />
            </Field>
            <Field label="Phone">
              <input className="form-input" value={data.phone}
                onChange={(e) => update("phone", e.target.value)} data-testid="wiz-phone"
                placeholder="optional" />
            </Field>
            <Field label="Gender">
              <select className="form-input" value={data.gender}
                onChange={(e) => update("gender", e.target.value)} data-testid="wiz-gender">
                <option value="">—</option>
                {GENDERS.map((g) => <option key={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Classification" required>
              <select className="form-input" value={data.staff_classification}
                onChange={(e) => update("staff_classification", e.target.value)} data-testid="wiz-classification">
                {ALL_CLASSIFICATIONS.map((c) => <option key={c}>{c}</option>)}
              </select>
              <p className="text-[12.5px] mt-1 text-slate-500" data-testid="wiz-classification-hint">
                {isCaregiver ? (
                  <span className="text-emerald-700">
                    Caregiver Team — appears in Staff Directory, Score Entry, and KPI Leaderboard.
                  </span>
                ) : data.staff_classification === "House Manager" ? (
                  <span className="text-sky-700">
                    Administration Team · operational home leadership — linked to assigned home(s),
                    shows on QA dashboards/reports, manages caregivers in their homes. NOT scored on the caregiver leaderboard.
                  </span>
                ) : (
                  <span className="text-sky-700">
                    Administration Team — appears in Users &amp; Access only. No caregiver scoring.
                  </span>
                )}
              </p>
            </Field>
            <Field label="Access role" required>
              <select className="form-input" value={data.access_role}
                onChange={(e) => update("access_role", e.target.value)} data-testid="wiz-role">
                {visibleRoles.map((r) => <option key={r}>{r}</option>)}
              </select>
              {data.access_role === "CEO" && (
                <p className="text-[12.5px] text-amber-700 mt-1">Only the CEO role can assign CEO. Use sparingly.</p>
              )}
            </Field>
          </div>
        </div>
      )}

      {/* STEP 2 — Home assignment */}
      {step === 2 && (
        <div className="space-y-3">
          <h3 className="text-h3">Home assignment</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Assigned home" required={requiresHome}>
              <select className="form-input" value={data.assigned_home}
                onChange={(e) => update("assigned_home", e.target.value)} data-testid="wiz-home"
                disabled={!requiresHome && data.access_role !== "Project Manager"}>
                <option value="">{requiresHome ? "Select home…" : "—"}</option>
                {accessibleHomes.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
              {!requiresHome && (
                <p className="text-[12.5px] text-slate-500 mt-1">Optional for {data.access_role}.</p>
              )}
            </Field>
            <Field label="Active status">
              <label className="inline-flex items-center gap-2 mt-2 text-sm text-slate-700">
                <input type="checkbox" checked={data.active_status}
                  onChange={(e) => update("active_status", e.target.checked)} data-testid="wiz-active" />
                User is active immediately
              </label>
            </Field>
            <Field label="Staff ID">
              <input className="form-input" value={data.staff_id_label}
                onChange={(e) => update("staff_id_label", e.target.value)} placeholder="optional, e.g. NV-001"
                data-testid="wiz-staff-id" />
            </Field>
            <Field label="Start date">
              <input type="date" className="form-input" value={data.start_date}
                onChange={(e) => update("start_date", e.target.value)} data-testid="wiz-start-date" />
            </Field>
          </div>
          {user.access_role === "House Manager" && (
            <p className="text-[12.5px] text-sky-700 bg-sky-50 border border-sky-200 rounded-md px-3 py-2">
              <ShieldCheck size={11} className="inline mr-1" />
              You can only assign new staff to your own home(s).
            </p>
          )}
        </div>
      )}

      {/* STEP 3 — Login setup */}
      {step === 3 && (
        <div className="space-y-3">
          <h3 className="text-h3">Login setup</h3>
          <Field label="Temporary password" required>
            <div className="relative">
              <input type={showPwd ? "text" : "password"} className="form-input pr-10"
                value={data.password} onChange={(e) => update("password", e.target.value)}
                data-testid="wiz-password" />
              <button type="button" onClick={() => setShowPwd((s) => !s)}
                className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-700"
                data-testid="wiz-password-toggle">
                {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="text-[12.5px] text-slate-500 mt-1">
              Default: <code className="bg-slate-100 px-1 rounded">{DEFAULT_TEMP_PWD}</code>. Min 6 characters.
            </p>
          </Field>
          <label className="flex items-start gap-2 text-sm text-slate-700 mt-3">
            <input type="checkbox" className="mt-0.5" checked={data.send_welcome_email}
              onChange={(e) => update("send_welcome_email", e.target.checked)}
              data-testid="wiz-send-email" />
            <span>
              <span className="font-medium">Send welcome email</span>
              <span className="block text-[12.5px] text-slate-500">
                <Mail size={10} className="inline mr-1" />
                Login link + temporary password emailed via Resend.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" className="mt-0.5" checked={data.force_password_change}
              onChange={(e) => update("force_password_change", e.target.checked)}
              data-testid="wiz-force-change" />
            <span>
              <span className="font-medium">Force password change on first login</span>
              <span className="block text-[12.5px] text-slate-500">
                Recommended — user must set a personal password before accessing the app.
              </span>
            </span>
          </label>
        </div>
      )}

      {/* STEP 4 — Review */}
      {step === 4 && (
        <div className="space-y-3" data-testid="wiz-confirm">
          <h3 className="text-h3">Review &amp; confirm</h3>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm grid grid-cols-2 gap-x-6 gap-y-2.5">
            <Row label="Name"             value={data.full_name} />
            <Row label="Email"            value={data.email} />
            <Row label="Phone"            value={data.phone || "—"} />
            <Row label="Classification"   value={data.staff_classification} />
            <Row label="Access role"      value={data.access_role} />
            <Row label="Assigned home"    value={data.assigned_home || "—"} />
            <Row label="Staff ID"         value={data.staff_id_label || "—"} />
            <Row label="Start date"       value={data.start_date || "—"} />
            <Row label="Active"           value={data.active_status ? "Yes" : "No"} />
            <Row label="Temp password"
                 value={data.password ? `Set (${data.send_welcome_email ? "will email" : "no email"})` : "—"} />
            <Row label="Force pwd change" value={data.force_password_change ? "Yes" : "No"} />
          </div>
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 flex items-start gap-2">
            <ShieldCheck size={14} className="mt-0.5 text-sky-700" />
            <span>The temporary password is hashed before storage. The plain value is returned to you once and (if checked) emailed to the user.</span>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-4 border-t border-slate-100">
        <button onClick={step === 1 ? onClose : prev}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-sm font-medium"
          data-testid="wiz-back">
          <ArrowLeft size={14} /> {step === 1 ? "Cancel" : "Back"}
        </button>
        {step < 4 ? (
          <button onClick={next} disabled={!stepValid}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="wiz-next">
            Next <ArrowRight size={14} />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="wiz-submit">
            <UserPlus size={14} /> {submitting ? "Creating…" : "Create staff"}
          </button>
        )}
      </div>

      <style>{`.form-input{ width:100%; font-size:0.875rem; border:1px solid rgb(226 232 240); border-radius:0.375rem; padding:0.5rem 0.75rem; background:#fff; } .form-input:focus{ outline:none; box-shadow:0 0 0 2px rgb(99 102 241 / .2); } .form-input:disabled{ background:#f8fafc; color:#94a3b8; }`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-small-caps">
        {label}{required && <span className="text-rose-500"> *</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <p className="text-[11.5px] uppercase tracking-wider font-semibold text-slate-400">{label}</p>
      <p className="font-medium text-slate-800 break-words">{value}</p>
    </div>
  );
}
